/**
 * Money.
 *
 * Every monetary value in this system is an integer number of cents. There are
 * no floats anywhere in the money path: not in the database, not on the wire,
 * not in the UI state. Floats are only ever produced at the very last moment by
 * `formatCents` for display, and are never read back.
 *
 * Quantities, however, ARE fractional (2.5 hours of labour, 1.75 lbs of
 * refrigerant), so the one place rounding happens is `lineTotal`. It rounds
 * half-away-from-zero, which is what a human doing this on paper does.
 */
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
export function assertCents(value, label = 'amount') {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
        throw new MoneyError(`${label} must be an integer number of cents, got ${value}`);
    }
    if (!Number.isSafeInteger(value)) {
        throw new MoneyError(`${label} exceeds safe integer range: ${value}`);
    }
    return value;
}
/** Round half away from zero. `roundHalfUp(2.5) === 3`, `roundHalfUp(-2.5) === -3`. */
export function roundHalfUp(value) {
    if (!Number.isFinite(value))
        throw new MoneyError(`cannot round non-finite value ${value}`);
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
export function addCents(...values) {
    return values.reduce((acc, v) => acc + assertCents(v), 0);
}
export function subCents(a, b) {
    return assertCents(a) - assertCents(b);
}
export function negateCents(a) {
    return -assertCents(a);
}
export function clampCents(value, min, max) {
    return Math.min(Math.max(assertCents(value), assertCents(min)), assertCents(max));
}
export function maxCents(...values) {
    return values.reduce((a, b) => (a > b ? a : b));
}
export function minCents(...values) {
    return values.reduce((a, b) => (a < b ? a : b));
}
/**
 * unit price x quantity. Quantity may be fractional; the result is rounded to
 * whole cents half-away-from-zero. This is the ONLY multiplication in the
 * money path that can lose a fraction of a cent, and it happens exactly once
 * per line item — never on a running total.
 */
export function lineTotal(unitPriceCents, quantity) {
    assertCents(unitPriceCents, 'unitPriceCents');
    if (!Number.isFinite(quantity))
        throw new MoneyError(`quantity must be finite, got ${quantity}`);
    return roundHalfUp(unitPriceCents * quantity);
}
/** Apply a basis-point rate to an amount, rounding half-away-from-zero. */
export function applyBps(amountCents, bps) {
    assertCents(amountCents, 'amountCents');
    if (!Number.isInteger(bps))
        throw new MoneyError(`bps must be an integer, got ${bps}`);
    return roundHalfUp((amountCents * bps) / 10_000);
}
/**
 * Split `amountCents` across `weights` proportionally, using the largest
 * remainder method so the parts always sum EXACTLY back to `amountCents`.
 * Used to spread a whole-invoice discount over taxable and non-taxable
 * buckets without leaking a cent into or out of the tax base.
 */
export function allocateCents(amountCents, weights) {
    assertCents(amountCents, 'amountCents');
    if (weights.length === 0)
        return [];
    if (weights.some((w) => w < 0))
        throw new MoneyError('allocation weights must be non-negative');
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    if (totalWeight === 0) {
        // Nothing to weight by: put it all on the first bucket so the sum still holds.
        return weights.map((_, i) => (i === 0 ? amountCents : 0));
    }
    const exact = weights.map((w) => (amountCents * w) / totalWeight);
    const floored = exact.map((v) => (v < 0 ? Math.ceil(v) : Math.floor(v)));
    let remainder = amountCents - floored.reduce((a, b) => a + b, 0);
    // Hand out the leftover cents to the largest fractional parts first.
    const order = exact
        .map((v, i) => ({ i, frac: Math.abs(v - (exact[i] < 0 ? Math.ceil(v) : Math.floor(v))) }))
        .sort((a, b) => b.frac - a.frac);
    const step = remainder < 0 ? -1 : 1;
    let cursor = 0;
    while (remainder !== 0 && order.length > 0) {
        const target = order[cursor % order.length].i;
        floored[target] = floored[target] + step;
        remainder -= step;
        cursor++;
    }
    return floored;
}
const DEFAULT_CURRENCY = 'USD';
/** Display only. Never parse the output of this back into a number. */
export function formatCents(value, currency = DEFAULT_CURRENCY, locale = 'en-US') {
    assertCents(value, 'amount');
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(value / 100);
}
/** Compact form for dense tables: `$1,234.50` -> `1,234.50`. */
export function formatCentsPlain(value, locale = 'en-US') {
    assertCents(value, 'amount');
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value / 100);
}
/** Parse user input ("1,234.56", "$12", "12.5") into cents. Returns null if unparseable. */
export function parseCents(input) {
    const cleaned = input.replace(/[^0-9.,\-]/g, '').replace(/,/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.')
        return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n))
        return null;
    return roundHalfUp(n * 100);
}
export function formatBps(bps) {
    return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}
/** Parse "8.25" or "8.25%" into 825 bps. Returns null if unparseable. */
export function parseBps(input) {
    const cleaned = input.replace(/[^0-9.\-]/g, '');
    if (cleaned === '')
        return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n))
        return null;
    return roundHalfUp(n * 100);
}
//# sourceMappingURL=money.js.map